import { Component, OnInit, AfterViewInit, HostListener, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';

interface SectionPoint {
  serialNumber: string;
  text: string;
  children?: SectionPoint[];
}

interface Section {
  serialNumber: string;
  label: string;
  description: SectionPoint[];
}

interface Category {
  label: string;
}

@Component({
  selector: 'app-terms-of-service',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HeaderComponent,
    FooterComponent
  ],
  templateUrl: './terms-of-service.component.html',
  styleUrl: './terms-of-service.component.scss',
})
export class TermsOfServiceComponent implements OnInit, AfterViewInit {
  @ViewChild('scrollContainer') scrollContainer?: ElementRef<HTMLElement>;

  activeCategory = 'Vision, Aims and Objectives';
  selectedItem: string | null = null;
  isStickyDropdown = false;

  sections: Section[] = [
    {
      serialNumber: '1',
      label: 'Vision, Aims and Objectives',
      description: [
        {
          serialNumber: '1',
          text: `The Government of Maharashtra is committed to fostering artificial intelligence ("AI")-driven innovation for social and economic progress in the agricultural sector. The Maharashtra Agriculture Exchange Platform ("NHA") has been established as a key instrument to operationalise this vision. The key aims and objectives of NHA envisaged in this Framework are to:`
        },
        {
          serialNumber: '1.1',
          text: `Democratise Access to Resources: NHA seeks to provide access to data that is not personal data, foundational AI models, and compute power to support widespread and collaborative AI development in the State of Maharashtra. NHA also strives to facilitate access to such data in multiple languages such as English, Marathi, etc. to ensure inclusive access and encourage innovation that reflects the linguistic, cultural, and socio-economic context of the State.`
        },
        {
          serialNumber: '1.2',
          text: `Foster AI Innovation: By lowering barriers to key AI infrastructure, NHA aims to facilitate the development of AI-driven solutions across agricultural sectors with a focus on indigenous use cases and applications.`
        },
        {
          serialNumber: '1.3',
          text: `Encourage Purposeful Solutions: Through this Framework, NHA seeks to provide a conducive environment for the development of relevant and efficient AI solutions, including citizen-centric and industry-facing use cases in agriculture.`
        },
        {
          serialNumber: '1.4',
          text: `Facilitate Research and Collaboration: NHA aims to provide an avenue to forge mission-driven partnerships between government departments, academia, research institutes, startups, civil society organisations and the industry to drive AI research, capacity-building, and innovation in the State.`
        }
      ]
    },
    {
      serialNumber: '2',
      label: 'Foundational Principles of NHA',
      description: [
        {
          serialNumber: '2.1',
          text: `Promote Privacy and Data Security: Providers and Users must use and access NHA, including any data in a secure and privacy-preserving manner in compliance with applicable laws (including the Digital Personal Data Protection Act, 2023) and this Framework.`
        },
        {
          serialNumber: '2.2',
          text: `Support trustworthy AI Innovation: NHA seeks to support the development of trustworthy AI Models and solutions by providing access to relevant datasets for model training. Through this Framework, NHA seeks to encourage Users to be guided by the foundational principles set out in this clause to develop trustworthy AI solutions with cross-sectoral use cases.`
        },
        {
          serialNumber: '2.3',
          text: `Ensure Transparency: NHA strives to ensure transparency by making Users' access to NHA, including data, contingent on well-defined safeguards under this Framework and applicable laws. NHA also enables Providers to control or regulate access to data based on these considerations.`
        },
        {
          serialNumber: '2.4',
          text: `Uphold Accountability: NHA endeavours to uphold accountability by clearly defining the roles and responsibilities of all participants, including Providers, Users, and the Operator, and requiring compliance with this Framework and applicable laws. NHA also provides a clear grievance redressal mechanism.`
        },
        {
          serialNumber: '2.5',
          text: `Inclusivity and Accessibility: NHA is designed to be inclusive and user-friendly. It incorporates accessible document formats and assistive technologies.`
        }
      ]
    },
    {
      serialNumber: '3',
      label: 'Scope and Applicability',
      description: [
        {
          serialNumber: '3.1',
          text: `This Framework will apply to the Operator, Providers, and Users and is legally binding as regards their access to, and use of, NHA and its Functionalities. In the event the User (including Providers) does not agree to the terms of this Framework, the User must immediately discontinue their use of NHA. Before proceeding with the registration and account creation on NHA, the User is required to review and consent to this Framework. If the User does not provide such consent, the account creation cannot be completed, and the Operator is not liable to provide access to NHA or any of its functionalities to such User.`
        },
        {
          serialNumber: '3.2',
          text: `The Framework will be subject to and superseded by any laws notified by the Government of Maharashtra or the Central Government.`
        }
      ]
    },
    {
      serialNumber: '4',
      label: 'Definitions',
      description: [
        {
          serialNumber: '4.1',
          text: `"AI Model" means any foundational, open-source machine learning model that is made available on NHA by a Provider or the Operator.`
        },
        {
          serialNumber: '4.2',
          text: `"account" means the login account created by a User under this Framework by registering on NHA, which is used by such User to access or utilise NHA and its Functionalities.`
        },
        {
          serialNumber: '4.3',
          text: `"anonymisation" in relation to data means such process(es) of transforming or converting data to a form through which any individual is not identifiable even if the information is combined with other information, after reasonably considering factors such as time, cost and technology. The terms "anonymise" or "anonymised" must be construed accordingly.`
        },
        {
          serialNumber: '4.4',
          text: `"content" means any material posted, displayed, or accessed on NHA, which includes its Functionalities.`
        },
        {
          serialNumber: '4.5',
          text: `"data" has the meaning assigned to it under the Digital Personal Data Protection Act, 2023.`
        },
        {
          serialNumber: '4.6',
          text: `"DPDPA" means the Digital Personal Data Protection Act, 2023 and any associated rules.`
        },
        {
          serialNumber: '4.7',
          text: `"Framework" means this Framework and SOPs issued under this Framework.`
        },
        {
          serialNumber: '4.8',
          text: `"Functionalities" mean data, that is not personal data, AI model, or compute power made available to any User on NHA, whether by Providers or the Operator. The term "Functionality" may be used to refer to any one of these services or tools.`
        },
        {
          serialNumber: '4.9',
          text: `"Government" means the Government of Maharashtra.`
        },
        {
          serialNumber: '4.10',
          text: `"Grievance Officer" means the officer designated under Clause 18 of the Framework.`
        },
        {
          serialNumber: '4.11',
          text: `"IT Act" means the Information Technology Act, 2000 and any associated rules.`
        },
        {
          serialNumber: '4.12',
          text: `"metadata" means structured data about data, which provides additional information that describes the content, context, structure, and administration of data and facilitates ease of finding and understanding the underlying data.`
        },
        {
          serialNumber: '4.13',
          text: `"Operator" means the Department of Agriculture, Government of Maharashtra.`
        },
        {
          serialNumber: '4.14',
          text: `"personal data" has the meaning assigned to it under the DPDPA.`
        },
        {
          serialNumber: '4.15',
          text: `"Provider" means a User who uploads, submits, shares, or otherwise makes available any Functionalities or other content on NHA.`
        },
        {
          serialNumber: '4.16',
          text: `"Standard Operating Procedure or SOP" refers to any set of user manuals, instructions, processes, best practices, and guidelines (by whatever name called) governing the access and use of NHA as may be issued by the Government under this Framework.`
        },
        {
          serialNumber: '4.17',
          text: `"NHA" refers to the platform owned and operated by the Operator that makes available the Functionalities contributed by Providers and any associated features or services, for use by Users.`
        },
        {
          serialNumber: '4.18',
          text: `"User" means any individual or entity, whether a company, partnership, proprietorship, society, association of persons or organisation, by whatever legal form constituted or whatever name called, who accesses and uses NHA pursuant to this Framework and includes a Provider.`
        }
      ]
    },
    {
      serialNumber: '5',
      label: 'Functionalities',
      description: [
        {
          serialNumber: '5.1',
          text: `Subject to the requirements of this Framework, the User may access the following Functionalities through NHA:`,
          children: [
            {
              serialNumber: 'a',
              text: `data across various domains made available by Providers or the Operator on NHA for purposes recognised under this Framework;`
            },
            {
              serialNumber: 'b',
              text: `foundational open-source AI models made available by Providers or the Operator, enabling users to train, fine-tune, and test AI solutions across varied sectors and use cases;`
            },
            {
              serialNumber: 'c',
              text: `compute resources made available by the Government to support AI experimentation, model training, and solution development.`
            }
          ]
        }
      ]
    },
    {
      serialNumber: '6',
      label: 'Accessing NHA and Functionalities',
      description: [
        {
          serialNumber: '6.1',
          text: `The Functionalities available on NHA may be classified as open access, restricted access or private access, as determined by the respective Provider of such Functionality. Such classification will be based on the following:`,
          children: [
            {
              serialNumber: 'a',
              text: `Open-access Functionalities are pre-viewable, viewable, and downloadable by all Users, without any restrictions;`
            },
            {
              serialNumber: 'b',
              text: `Restricted-access Functionalities are viewable and downloadable only by Users who have been granted access by the Provider who has made such Functionalities available on NHA; and`
            },
            {
              serialNumber: 'c',
              text: `Private-access Functionalities are pre-viewable, viewable, and downloadable only by the Provider who has made such Functionalities available on NHA.`
            }
          ]
        },
        {
          serialNumber: '6.2',
          text: `Users may access the following features of NHA without creating an account on NHA:`,
          children: [
            {
              serialNumber: 'a',
              text: `previewing data, which refers to viewing excerpts or limited samples of actual datasets; and`
            },
            {
              serialNumber: 'b',
              text: `viewing metadata of data and AI Models.`
            }
          ]
        },
        {
          serialNumber: '6.3',
          text: `Users can access the following Functionalities and undertake the following functions only after creating an account on NHA:`,
          children: [
            {
              serialNumber: 'a',
              text: `downloading data;`
            },
            {
              serialNumber: 'b',
              text: `downloading AI Models;`
            },
            {
              serialNumber: 'c',
              text: `accessing compute power; and`
            },
            {
              serialNumber: 'd',
              text: `operating as a Provider.`
            }
          ]
        },
        {
          serialNumber: '6.4',
          text: `A Provider must be a resident of India, or an entity (by whatever name called) incorporated or having its principal place of business in India.`
        },
        {
          serialNumber: '6.5',
          text: `The procedure and requirements for any User to create an account, access, and use NHA or any of its Functionalities, including any eligibility requirements, and the requirement to provide supporting documents and such other matters as set out in Clause 10, will be prescribed through SOP(s). It is clarified that the procedure and requirements may vary depending on the nature of the Functionality and the category of User.`
        },
        {
          serialNumber: '6.6',
          text: `Where a User is a company or other organisation, it is assumed that the individual creating an account on behalf of such company or organisation is duly authorised to do so. The company or organisation will be bound by this Framework (as a User) and will be fully responsible for the actions and omissions of the individual acting on its behalf on NHA.`
        },
        {
          serialNumber: '6.7',
          text: `Users may delete their accounts at any time. The account is not transferable between Users and Providers. However, the Functionalities and content of a User account that belongs to a company or other organisation is transferable between different organisational managers, upon adequate authorisation from such company or organisation in accordance with this Framework. For the purpose of this Framework, organisational managers mean individuals who create and operate the User account on behalf of such company or other organisation with due authorisation from such company in accordance with this Framework.`
        },
        {
          serialNumber: '6.8',
          text: `Any personal data shared or collected during account creation will be processed in accordance with applicable laws.`
        }
      ]
    },
    {
      serialNumber: '7',
      label: 'Permitted and Prohibited Uses of NHA',
      description: [
        {
          serialNumber: '7.1',
          text: `The User must use NHA in accordance with the Framework, applicable laws, and terms agreed between the User and the Provider of the relevant Functionality.`
        },
        {
          serialNumber: '7.2',
          text: `The User must not make available any personal data on NHA, except as required to create an account on NHA under Clause 6.`
        },
        {
          serialNumber: '7.3',
          text: `The User must not utilise the data made available through NHA in any manner that is in violation of applicable data protection law and in a manner that enables identification of an individual to whom such data pertains.`
        },
        {
          serialNumber: '7.4',
          text: `No User and Provider must make any content and Functionality, as may be applicable, available on NHA which is in violation of any law, including the IT Act. This prohibition extends, but is not limited, to any content and Functionality that is obscene, pornographic, paedophilic, harmful to a child, invasive of another's privacy, insulting or harassing, relating to or encouraging money laundering, promoting enmity between different groups, deceitful or misleading as to the origin of the message, constitutes misinformation, is patently false or misleading, impersonates any other person, threatens the unity, integrity, defence, security or sovereignty of India, friendly relations with foreign states, or public order, or causes incitement to the commission of any cognisable offence, or prevents investigation of any offence, or is insulting any other nation, or contains a computer virus or any other computer code that may destroy the functionality of NHA.`
        },
        {
          serialNumber: '7.5',
          text: `The User must not undertake any activity that can damage, disable, or overburden NHA servers or networks or interfere with another User's access to NHA or Functionalities.`
        },
        {
          serialNumber: '7.6',
          text: `The User must not attempt to gain unauthorised access to any Functionalities or accounts on NHA.`
        },
        {
          serialNumber: '7.7',
          text: `The User must not utilise or deploy any network monitoring or discovery software to determine the site architecture of NHA or extract information about other Users or their identities.`
        }
      ]
    },
    {
      serialNumber: '8',
      label: 'Roles and Responsibilities of Users and Providers',
      description: [
        {
          serialNumber: '8.1',
          text: `The User has the following roles and responsibilities on NHA:`,
          children: [
            {
              serialNumber: 'a',
              text: `The User must establish such technological and operational safeguards for privacy and cybersecurity to ensure the safety of data accessed through NHA, as may be prescribed in this Framework including any SOP(s).`
            },
            {
              serialNumber: 'b',
              text: `The User must ensure that they have lawful ownership, including over the intellectual property ("IP"), of the content made available by them on NHA and such content must not infringe any third-party rights or violate the terms of any agreement.`
            },
            {
              serialNumber: 'c',
              text: `The User is solely and entirely responsible and liable for any solutions developed by them using NHA or its Functionalities.`
            },
            {
              serialNumber: 'd',
              text: `The User is solely responsible for ensuring that (i) any development of AI solutions or products by them based on data accessed on NHA; or (ii) any further training, development, or additions undertaken by them to AI Models available on NHA are privacy-protecting, safe, accessible, duly tested and validated, accurate, and compliant with applicable laws. The User remains solely responsible for these actions regardless of whether they undertake them on or outside NHA by downloading Functionalities.`
            },
            {
              serialNumber: 'e',
              text: `The User must comply with all applicable laws and this Framework, including SOPs.`
            },
            {
              serialNumber: 'f',
              text: `The User is solely and entirely responsible and liable for any harm or damage to them or any third party resulting from such User's access to or use of the Functionalities or content, including any features like downloading.`
            },
            {
              serialNumber: 'g',
              text: `The User is solely and entirely responsible for the manner in which the User makes use of any Functionality made available on NHA, including any actions taken after downloading or exporting such Functionality from NHA, as may be applicable. The User acknowledges that any such use is undertaken at their discretion and risk, and they are fully accountable for any outcome from such use.`
            }
          ]
        },
        {
          serialNumber: '8.2',
          text: `In addition to the obligations set out in Clause 8.1, the Provider has the following additional roles and responsibilities on NHA:`,
          children: [
            {
              serialNumber: 'a',
              text: `The Provider is responsible for determining the access and usage of the Functionalities made available by them on NHA, including whether such Functionalities are designated as open access, restricted access or private access and imposing any conditions on the User for accessing or using such Functionalities.`
            },
            {
              serialNumber: 'b',
              text: `The Provider must ensure that they have lawful ownership, including over the IP, of the Functionalities or content contributed by them on NHA and such Functionalities or content must not infringe any third-party rights or violate the terms of any agreement.`
            },
            {
              serialNumber: 'c',
              text: `The Provider must ensure the completeness and accuracy of all data made available by them.`
            },
            {
              serialNumber: 'd',
              text: `The Provider must ensure that all data contributed by them is appropriately secured and compliant with applicable laws, including the IT Act and DPDPA. Where anonymisation techniques are employed, the Provider must ensure that such techniques are robust and secure to prevent re-identification.`
            },
            {
              serialNumber: 'e',
              text: `The Provider will be solely and entirely responsible and liable for ensuring that AI Models made available by them are privacy-protecting, safe, accessible, duly tested and validated, accurate, and compliant with applicable laws.`
            }
          ]
        }
      ]
    },
    {
      serialNumber: '9',
      label: 'Roles and Responsibilities of the Operator',
      description: [
        {
          serialNumber: '9.1',
          text: `The Operator makes available the Functionalities and content on an "as is" and "as available" basis.`
        },
        {
          serialNumber: '9.2',
          text: `The Operator has no role in screening, endorsing, or ensuring the accuracy, reliability, security, validity, benefit, availability, or compliance with applicable laws or agreements, of the Functionalities, content or any service made available by Providers and accessed by Users through NHA.`
        },
        {
          serialNumber: '9.3',
          text: `The Operator has no role in screening, endorsing, or ensuring the accuracy, reliability, security, validity, benefit, availability, or compliance with applicable laws or agreements, of the solutions or products developed by Users using the Functionalities made available on NHA.`
        },
        {
          serialNumber: '9.4',
          text: `Any terms or conditions (by whatever name called) agreed between a User of a Functionality and the Provider of such Functionality in relation to the access and use of such Functionality made available through NHA is solely between such User and Provider and must not contravene this Framework. The Operator is not a Party to such arrangements and does not endorse such terms or conditions. The Operator disclaims all responsibility for any damage or harm caused by or arising out of such terms or conditions.`
        },
        {
          serialNumber: '9.5',
          text: `The Operator has no role in determining, and assumes no responsibility for, the content, accuracy, privacy policies, or practices of any third-party platforms that Users access, or are redirected to, through links or application programming interfaces on NHA. Any User engagement with third-party content or on third-party platforms is beyond the purview of NHA and does not constitute the responsibility of the Operator.`
        },
        {
          serialNumber: '9.6',
          text: `The Operator is not responsible for any damage or harm caused by the User's access to, or use of, NHA. The Operator further disclaims all warranties or guarantees of any kind, express or implied, including but not limited to the implied warranties of merchantability, non-infringement, fitness for a particular purpose, or otherwise.`
        }
      ]
    },
    {
      serialNumber: '10',
      label: 'Operationalising NHA: SOPs',
      description: [
        {
          serialNumber: '10.1',
          text: `To give effect to this Framework, the Government through the Department of Agriculture will issue SOPs, including for the following purposes:`,
          children: [
            {
              serialNumber: 'a',
              text: `Prescribing the procedure and requirements for registering or creating an account, accessing, and using NHA and its Functionalities, including the requirement to provide any additional documents, under Clause 6; Specifying the eligibility criteria, conditions and charges if any, for accessing any of the Functionalities on NHA;`
            },
            {
              serialNumber: 'b',
              text: `Specifying how to search, filter, and view metadata or preview data;`
            },
            {
              serialNumber: 'c',
              text: `Setting out steps to request download access to Functionalities;`
            },
            {
              serialNumber: 'd',
              text: `Setting out steps for uploading and publishing Functionalities;`
            },
            {
              serialNumber: 'e',
              text: `Setting out any other requirements relating to the governance and operations of NHA necessary to further the objectives of the Framework; and`
            },
            {
              serialNumber: 'f',
              text: `Updating any SOP(s) issued by it from time to time.`
            }
          ]
        }
      ]
    },
    {
      serialNumber: '11',
      label: 'Intellectual Property',
      description: [
        {
          serialNumber: '11.1',
          text: `The Operator retains complete and exclusive ownership and associated IP rights of NHA, its design, code, interfaces, trademarks, and logo, including all improvements to NHA and these features. All rights not expressly granted under the Framework are reserved and retained by the Operator.`
        },
        {
          serialNumber: '11.2',
          text: `The Provider grants to the Operator a worldwide, royalty-free and non-exclusive license to use and make derivative works of the Functionalities and content for the purposes of operating NHA in accordance with this Framework.`
        },
        {
          serialNumber: '11.3',
          text: `The Operator grants to all Users a limited, revocable, non-exclusive, non-sublicensable, and non-transferable license to access and use NHA in accordance with this Framework.`
        },
        {
          serialNumber: '11.4',
          text: `The Operator does not claim any IP rights on the Functionalities or content made available or any AI solutions developed by Users on NHA. These rights are retained by the relevant User.`
        },
        {
          serialNumber: '11.5',
          text: `The Provider may choose to subject Functionalities made available by them to certain license terms to be abided by Users. Such license terms are not modified by this Framework, except that all limitations of liabilities and disclaimers contained in this Framework apply to any Functionality that is the subject matter of such license terms as well.`
        }
      ]
    },
    {
      serialNumber: '12',
      label: 'Confidentiality',
      description: [
        {
          serialNumber: '12.1',
          text: `Users are responsible for maintaining the confidentiality of their account information and activity. The Operator is not liable for any loss or damage arising from Users' failure to secure their accounts.`
        },
        {
          serialNumber: '12.2',
          text: `If the User suspects that a third party is aware of their account information, the User must inform the Operator and take steps to update the account information immediately.`
        },
        {
          serialNumber: '12.3',
          text: `Users must notify the Operator immediately of any unauthorised use or breaches of their account information or Functionalities made available by them.`
        }
      ]
    },
    {
      serialNumber: '13',
      label: 'Term and Termination',
      description: [
        {
          serialNumber: '13.1',
          text: `The User may terminate the User's account at its sole discretion.`
        },
        {
          serialNumber: '13.2',
          text: `The Operator may suspend or terminate a User account at its sole discretion, whether in case of any contravention of this Framework or otherwise, with or without cause.`
        },
        {
          serialNumber: '13.3',
          text: `The Operator will not be liable for any damage, losses, claims, or damages that may arise out of such account termination.`
        },
        {
          serialNumber: '13.4',
          text: `Upon termination of any User account, the Operator will use commercially reasonable efforts to delete all content contributed by the User within 180 days, except where applicable laws require retention.`
        }
      ]
    },
    {
      serialNumber: '14',
      label: 'Indemnity',
      description: [
        {
          serialNumber: '14.1',
          text: `The User is solely and exclusively responsible for their use of NHA. The User must indemnify, defend and hold harmless the Operator from and against all liability, losses, claims, and damages, including legal fees, resulting from the User's use of NHA, including without limitation:`,
          children: [
            {
              serialNumber: 'a',
              text: `the User's violation of this Framework;`
            },
            {
              serialNumber: 'b',
              text: `the User's violation of any third-party right under any applicable law or agreement, including privacy infringements or IP rights;`
            },
            {
              serialNumber: 'c',
              text: `the User's breach of any applicable laws;`
            },
            {
              serialNumber: 'd',
              text: `any damage or harm, whether direct or consequential, arising out of any terms of use executed by and between Users and Providers;`
            },
            {
              serialNumber: 'e',
              text: `any damage or harm, whether direct or consequential, arising out of the Functionalities made available by a Provider on NHA; and`
            },
            {
              serialNumber: 'f',
              text: `any unauthorised, improper, illegal or wrongful use of the User's account by any person, including a third party, whether or not authorised or permitted by the User.`
            }
          ]
        },
        {
          serialNumber: '14.2',
          text: `The User's obligation to indemnify will survive any termination of the User's account on NHA.`
        }
      ]
    },
    {
      serialNumber: '15',
      label: 'Notices',
      description: [
        {
          serialNumber: '15.1',
          text: `If any User finds any Functionalities or content to be in violation of this Framework or any applicable laws, including the IT Act, such User may notify the Operator through the grievance redressal mechanism available on NHA.`
        },
        {
          serialNumber: '15.2',
          text: `Upon receipt of such a complaint from a User, the Operator will proceed in accordance with applicable laws, including the IT Act.`
        }
      ]
    },
    {
      serialNumber: '16',
      label: 'Transparency and Disclosures',
      description: [
        {
          serialNumber: '16.1',
          text: `The Operator may at such intervals as deemed appropriate by the Government publish relevant information about NHA and its operation, including the number and nature of Functionalities available on NHA, the number of Providers and Users, and AI solutions under research or development.`
        }
      ]
    },
    {
      serialNumber: '17',
      label: 'Modifications and Amendments',
      description: [
        {
          serialNumber: '17.1',
          text: `The Operator may review and amend or modify this Framework periodically, which will be effective within 7 days from the date of posting such revised terms. Such amended or modified Framework will apply to the Operator, Providers, and Users of NHA once it is effective.`
        },
        {
          serialNumber: '17.2',
          text: `Users are requested to read, understand, and consent to such amended or modified Framework, which will apply to the Operator, Providers, and Users of NHA once it is effective. In the event Users (including Providers) do not agree to such amended or modified Framework, Users must discontinue their use of NHA. NHA will not be liable to the User for discontinuance or suspension of access to NHA in the event the User does not consent to such revised terms.`
        },
        {
          serialNumber: '17.3',
          text: `Any actions undertaken by the Operator or the Grievance Officer under a previous version of the Framework will not be affected by any subsequent modifications or amendments.`
        }
      ]
    },
    {
      serialNumber: '18',
      label: 'Grievance Redressal',
      description: [
        {
          serialNumber: '18.1',
          text: 'For the redressal of any grievance arising in connection with this Framework or the use of any data thereunder, the User may reach out to the grievance officer of the Operator ("<strong>Grievance Officer</strong>"):<br><br>Name of the Grievance Officer : [Name]<br>E-mail ID: <a href="mailto:grievance-nha-agri@maharashtra.gov.in" style="color: #007bff; text-decoration: underline;">grievance-nha-agri@maharashtra.gov.in</a><br>Contact Number: [Contact Number]'
        }
      ]
    },
    {
      serialNumber: '19',
      label: 'Governing Law and Jurisdiction',
      description: [
        {
          serialNumber: '19.1',
          text: `This Framework will be governed by and construed in accordance with the laws of India. Any disputes arising out of or in connection with NHA or this Framework will be subject to the exclusive jurisdiction of the courts of Maharashtra.`
        }
      ]
    }
  ];

  categories: Category[] = [
    { label: 'Vision, Aims and Objectives' },
    { label: 'Foundational Principles of NHA' },
    { label: 'Scope and Applicability' },
    { label: 'Definitions' },
    { label: 'Functionalities' },
    { label: 'Accessing NHA and Functionalities' },
    { label: 'Permitted and Prohibited Uses of NHA' },
    { label: 'Roles and Responsibilities of Users and Providers' },
    { label: 'Roles and Responsibilities of the Operator' },
    { label: 'Operationalising NHA: SOPs' },
    { label: 'Intellectual Property' },
    { label: 'Confidentiality' },
    { label: 'Term and Termination' },
    { label: 'Indemnity' },
    { label: 'Notices' },
    { label: 'Transparency and Disclosures' },
    { label: 'Modifications and Amendments' },
    { label: 'Grievance Redressal' },
    { label: 'Governing Law and Jurisdiction' }
  ];

  constructor(private router: Router) {}

  ngOnInit(): void {
    // Initialize selected item and active category to first category
    this.selectedItem = this.categories[0].label;
    this.activeCategory = this.categories[0].label;
    
    // Scroll to top when component loads to ensure first section is visible
    setTimeout(() => {
      this.scrollToTop();
    }, 0);
  }

  ngAfterViewInit(): void {
    this.updateActiveCategoryOnScroll();
  }

  /** Called by (scroll) on section and by window:scroll on smaller screens */
  onScroll(): void {
    const container = this.scrollContainer?.nativeElement;
    const scrollPosition = (container && this.isSectionScrollContainer(container))
      ? container.scrollTop
      : (window.pageYOffset ?? document.documentElement.scrollTop);
    this.isStickyDropdown = scrollPosition > 100;
    this.updateActiveCategoryOnScroll();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.onScroll();
  }

  private isSectionScrollContainer(container: HTMLElement | undefined): boolean {
    return !!container && container.scrollHeight > container.clientHeight;
  }

  updateActiveCategoryOnScroll(): void {
    const offset = 150; // Offset to determine which section is active (matches scrollToSection offset)
    const container = this.scrollContainer?.nativeElement;
    const useSectionScroll = this.isSectionScrollContainer(container);
    const scrollPosition = useSectionScroll
      ? (container!.scrollTop)
      : (window.pageYOffset ?? document.documentElement.scrollTop);

    for (let i = this.sections.length - 1; i >= 0; i--) {
      const section = this.sections[i];
      const sectionId = this.sanitizeId(section.label);
      const element = document.getElementById(sectionId);
      if (!element) continue;

      let elementTop: number;
      if (useSectionScroll && container) {
        // Section is scroll container: position relative to scrollable content
        elementTop = container.scrollTop + (element.getBoundingClientRect().top - container.getBoundingClientRect().top);
      } else {
        const elementRect = element.getBoundingClientRect();
        elementTop = elementRect.top + scrollPosition;
      }

      if (scrollPosition + offset >= elementTop) {
        this.activeCategory = section.label;
        this.selectedItem = section.label;
        break;
      }
    }
  }

  onDropdownChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const selectedLabel = target.value;
    if (selectedLabel) {
      this.isStickyDropdown = true;
      this.scrollToSection(selectedLabel);
    }
  }

  filterSections(section: string): void {
    this.activeCategory = section;
    this.scrollToSection(section);
  }

  private scrollToTop(): void {
    const container = this.scrollContainer?.nativeElement;
    if (container && this.isSectionScrollContainer(container)) {
      container.scrollTo({ top: 0, behavior: 'auto' });
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }

  scrollToSection(label: string): void {
    const sectionId = this.sanitizeId(label);
    const element = document.getElementById(sectionId);
    if (!element) return;

    const offset = 150;
    const container = this.scrollContainer?.nativeElement;
    const useSectionScroll = this.isSectionScrollContainer(container);

    if (useSectionScroll && container) {
      // Scroll within the section container so right sidebar highlights match
      const elementTopInContent = container.scrollTop + (element.getBoundingClientRect().top - container.getBoundingClientRect().top);
      const targetScrollTop = Math.max(0, elementTopInContent - offset);
      container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
      return;
    }

    // Window scroll (e.g. smaller screens)
    const elementRect = element.getBoundingClientRect();
    const elementTop = elementRect.top + window.pageYOffset;
    const elementBottom = elementTop + elementRect.height;
    const offsetPosition = elementTop - offset;
    const sectionIndex = this.sections.findIndex(s => s.label === label);
    const isLastSection = sectionIndex === this.sections.length - 1;
    const viewportHeight = window.innerHeight;

    let finalScroll = offsetPosition;
    if (isLastSection) {
      const maxScrollForSection = elementBottom - viewportHeight;
      finalScroll = Math.min(offsetPosition, Math.max(offsetPosition, maxScrollForSection));
      finalScroll = Math.min(finalScroll, elementBottom - viewportHeight);
    }

    window.scrollTo({ top: finalScroll, behavior: 'smooth' });
  }

  navigateToHome(): void {
    this.router.navigate(['/']);
  }

  sanitizeId(label: string): string {
    // More robust ID sanitization
    return label
      .toLowerCase()
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters except spaces
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  }
}
